import { SyntheticEvent, useEffect, useLayoutEffect, useState } from "react";
import { IonButton } from "@ionic/react";
import { translate } from "components/string-table";
import { Alert, App, AuthLayout, BuildProfile, EmailField, PasswordField, useAuth } from "main";
import { RouteComponentProps } from "react-router";
import { generateSwingSupportURL } from "swing-utils";

import styles from "./LoginWithPassword.module.css";

type LoginWithPasswordProps = {
  app: App;
  buildProfile?: BuildProfile;
  forgotPasswordPath: string;
  history: RouteComponentProps["history"];
  isWeb?: boolean;
  location: RouteComponentProps["location"];
  onEmailEdit: (email: string) => void;
  platform: string;
};

export function LoginWithPassword(props: LoginWithPasswordProps) {
  const { app, buildProfile, forgotPasswordPath, history, isWeb, location, onEmailEdit, platform } =
    props;

  /***** Hooks *****/
  const { forgotPasswordRequest, signIn } = useAuth();
  const locationState = location.state as { email: string; isPasswordReset: boolean } | undefined;

  // redirect if no email exists on location state (e.g when directly visiting /login)
  useLayoutEffect(() => {
    if (!locationState || !locationState.email) {
      return history.push("/");
    }
  }, [history, locationState]);

  const [form, setForm] = useState<LoginWithPasswordFormState>({
    email: locationState && locationState.email ? locationState.email : "",
    password: "",
  });

  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const [passwordError, setPasswordError] = useState("");
  const isPasswordReset = (locationState && locationState.isPasswordReset) || false;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // when coming back from successful password reset action
  useEffect(() => {
    // clear error
    setPasswordError("");
    // clear password
    setForm((prev) => ({ email: prev.email, password: "" }));
  }, [isPasswordReset, locationState?.email]);

  const onLogin = async (e: SyntheticEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // format email before sending to Cognito. Emails are stored lowercase in Cognito User Pool causing
    // a failure to occur when attempting to retrieve the user by an email that does not match expected case
    const loginPayload = {
      email: form.email.toLowerCase().trim(),
      password: form.password,
    };

    await signIn({ provider: "cognito", user: loginPayload }).catch((e) => {
      switch (e.message) {
        case "Password attempts exceeded":
          setPasswordError(translate("LOGIN_WITH_PASSWORD_LOGIN_ATTEMPTS_EXCEEDED"));
          break;
        default:
          setPasswordError(translate("LOGIN_WITH_PASSWORD_LOGIN_INCORRECT_PASSWORD"));
          break;
      }
    });

    setIsSubmitting(false);
  };

  const onForgotPassword = () => {
    history.push({
      pathname: forgotPasswordPath,
      state: {
        email: form.email,
      },
    });
  };

  const generateValidationCodeEmail = () => {
    forgotPasswordRequest(form.email)
      .then(() => {
        onForgotPassword();
        console.info("Email sent.");
      })
      .catch((err: Error) => {
        console.error("UI - forgotPasswordRequest error: ", err);
        setShowErrorMessage(true);
      });
  };

  return (
    <AuthLayout
      app={app}
      isWeb={isWeb}
      title={`${translate("LOGIN_WITH_PASSWORD_LOGIN_WELCOME_BACK_MESSAGE")}!`}
    >
      <form className={styles.form} onSubmit={(e) => onLogin(e)}>
        <div
          className={
            showErrorMessage || (isPasswordReset && !showErrorMessage) ? styles.spacing : ""
          }
        >
          {showErrorMessage && <DefaultLoginErrorBanner />}
          {isPasswordReset && !showErrorMessage && (
            <Alert color="success">
              {translate("LOGIN_WITH_PASSWORD_RESET_PASSWORD_SUCCESS_MESSAGE")}.
            </Alert>
          )}
        </div>
        <div className={styles.spacing}>
          <EmailField
            app={app}
            buildProfile={buildProfile}
            email={form.email}
            onEmailEdit={onEmailEdit}
            platform={platform}
            readOnly={true}
          />
        </div>
        <div className={styles.spacing}>
          <PasswordField
            passwordLabel={translate("LOGIN_WITH_PASSWORD_LOGIN_PASSWORD_LABEL")}
            onPasswordInput={(value: string) =>
              setForm((prevForm) => ({ ...prevForm, password: value }))
            }
            error={passwordError}
            password={form.password}
          />
        </div>
        <IonButton
          data-testid="cognito-submit-button"
          type="submit"
          size="default"
          expand="block"
          className={styles.loginButton}
          disabled={!isFormValid(form) || isSubmitting}
        >
          {translate("LOGIN_WITH_PASSWORD_LOGIN_BUTTON_LABEL")}
        </IonButton>
      </form>
      <button
        css={{
          background: "none",
          fontWeight: "var(--swing-font-medium)",
          fontSize: 12,
          cursor: "pointer",
          color: "var(--ion-color-primary)",
          textAlign: "center",
        }}
        onClick={generateValidationCodeEmail}
        data-testid="forgot-password-button" // For Storybook API failure testing
        name="button-password"
      >
        {translate("LOGIN_WITH_PASSWORD_LOGIN_FORGOT_PASSWORD_LABEL")}?
      </button>
    </AuthLayout>
  );
}

type LoginWithPasswordFormState = {
  email: string;
  password: string;
};

export function DefaultLoginErrorBanner() {
  const contactSupportLink = generateSwingSupportURL("/hc/en-us/requests/new");
  return (
    <Alert color="danger" showIcon title={translate("LOGIN_WITH_PASSWORD_LOGIN_ERROR_TITLE")}>
      <p
        style={{ margin: 0 }}
        dangerouslySetInnerHTML={{
          __html: translate("LOGIN_WITH_PASSWORD_LOGIN_ERROR_MESSAGE", { contactSupportLink }),
        }}
      />
    </Alert>
  );
}

function isFormValid(form: LoginWithPasswordFormState) {
  const { password } = form;
  return password.length !== 0;
}
